import React from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Alert,
  Chip,
} from '@mui/material';
import {
  CheckCircle,
  Schedule,
  Assignment,
} from '@mui/icons-material';

const ExamComplete: React.FC = () => {
  const { assignmentId } = useParams<{ assignmentId: string }>();

  return (
    <Box sx={{ minHeight: '100vh', backgroundColor: '#f8fafc', p: 4 }}>
      <Box maxWidth="600px" mx="auto">
        {/* Success Header */}
        <Card sx={{ borderRadius: '16px', mb: 4, background: 'linear-gradient(135deg, #4caf50 0%, #45a049 100%)', color: 'white' }}>
          <CardContent sx={{ textAlign: 'center', py: 6 }}>
            <CheckCircle sx={{ fontSize: 80, mb: 2 }} />
            <Typography variant="h3" fontWeight={700} gutterBottom>
              🎉 Exam Completed!
            </Typography>
            <Typography variant="h6" sx={{ opacity: 0.9 }}>
              Your responses have been submitted successfully
            </Typography>
          </CardContent>
        </Card>

        {/* Exam Summary */}
        <Card sx={{ borderRadius: '12px', mb: 4 }}>
          <CardContent>
            <Typography variant="h5" fontWeight={600} gutterBottom>
              📊 Exam Summary
            </Typography>
            
            <Box display="flex" gap={3} sx={{ mt: 2, flexDirection: { xs: 'column', md: 'row' } }}>
              <Box flex={1} textAlign="center" p={2} sx={{ backgroundColor: 'primary.light', borderRadius: '8px' }}>
                <Assignment sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Assignment ID
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {assignmentId}
                </Typography>
              </Box>
              <Box flex={1} textAlign="center" p={2} sx={{ backgroundColor: 'success.light', borderRadius: '8px' }}>
                <Schedule sx={{ fontSize: 40, color: 'success.main', mb: 1 }} />
                <Typography variant="h6" fontWeight={600}>
                  Completed At
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {new Date().toLocaleString()}
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Next Steps */}
        <Card sx={{ borderRadius: '12px', mb: 4 }}>
          <CardContent>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              📋 What Happens Next?
            </Typography>
            
            <Alert severity="info" sx={{ mb: 3 }}>
              <Typography variant="body2">
                Your exam responses are being processed. Results will be available within 24-48 hours.
              </Typography>
            </Alert>

            <Box display="flex" flexDirection="column" gap={2}>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip label="1" color="primary" />
                <Typography variant="body1">
                  Your responses are being reviewed and scored
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip label="2" color="primary" />
                <Typography variant="body1">
                  Results will be sent to your registered email address
                </Typography>
              </Box>
              <Box display="flex" alignItems="center" gap={2}>
                <Chip label="3" color="primary" />
                <Typography variant="body1">
                  You can check your results in the dashboard
                </Typography>
              </Box>
            </Box>
          </CardContent>
        </Card>

        {/* Thank You Message */}
        <Card sx={{ borderRadius: '12px' }}>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6" gutterBottom>
              Thank you for completing the assessment!
            </Typography>
            <Typography variant="body2" color="text.secondary">
              We appreciate your time and effort in taking this exam.
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default ExamComplete;
